import {
    Box,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Link
} from 'react-router-dom';
import type {
    Settings
} from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import Section from '../Section';

import Button from '@components/typed/Button';
import CategoriesCarousel, {
    CATEGORY_ID_PLACEHOLDER
} from '@components/typed/CategoriesCarousel';
import InlineLoader from '@components/typed/InlineLoader';
import Typography from '@components/typed/Typography';

import {
    customerDataMicroserviceRequest
} from '@services/api/customer-data';

import type {
    SICCodeCategory
} from '@types';

import type {
    SupportersProps
} from './types';

const mobile: Settings = {
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: 3,
    slidesToShow: 3.5,
    speed: 500
};

const desktop: Settings = {
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: 5,
    slidesToShow: 5,
    speed: 500
};

const desktopXL: Settings = {
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: 6,
    slidesToShow: 6,
    speed: 500
};

const Supporters: FC<SupportersProps> = (props) => {
    const {
        divider,
        organizationDetails,
        shouldDisplayBottomDivider
    } = props;

    const [
        categoriesList,
        setCategoriesList
    ] = useState<SICCodeCategory[]>([]);
    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isDesktopXL = useMediaQuery(theme.breakpoints.up('xl'));

    const settings = useMemo(() => {
        if (isMobile) {
            return mobile;
        }

        if (isDesktopXL) {
            return desktopXL;
        }

        return desktop;
    }, [
        isMobile,
        isDesktopXL
    ]);

    const sicRequest = useRef<Promise<void> | null>(null);

    const fetchSICCodes = useCallback(async () => {
        if (!organizationDetails) {
            return undefined;
        }

        if (categoriesList.length) {
            return undefined;
        }

        if (sicRequest.current) {
            return sicRequest.current;
        }

        sicRequest.current = (async () => {
            try {
                setIsLoading(true);

                const {
                    data: fetchedCategoriesList
                } = await customerDataMicroserviceRequest<SICCodeCategory[]>(
                    'get',
                    process.env.SPONSOR_MANAGEMENT_API_URL,
                    `organizations/${organizationDetails.salesforce_id}/parent-sic-categories`
                );

                setCategoriesList(fetchedCategoriesList);
            } catch {
                setCategoriesList([]);
            } finally {
                setIsLoading(false);
                sicRequest.current = null;
            }
        })();

        return sicRequest.current;
    }, [
        setCategoriesList,
        setIsLoading,
        organizationDetails,
        categoriesList.length
    ]);

    useEffect(() => {
        if (organizationDetails) {
            fetchSICCodes();
        }
    }, [
        fetchSICCodes,
        organizationDetails
    ]);

    if (isLoading) {
        return <InlineLoader height={330} />;
    }

    if (!categoriesList.length) {
        return null;
    }

    return (
        <>
            {divider}
            <Section
                horizontalPadding={'deep'}
                mb={{
                    md: 5,
                    xs: 2
                }}
            >
                <Typography variant={'h3'}>
                    <b>{'Discover'}</b>&nbsp;{`businesses that support ${organizationDetails.physical_title}`}
                </Typography>
                <Button
                    component={Link}
                    isMobileFullWidth
                    sx={{
                        backgroundColor: theme.colors.fadedJade,
                        color: `${theme.colors.white} !important`,
                        mt: 4
                    }}
                    to={`/organization/${organizationDetails.slug}/supporters`}
                >
                    {'View All'}
                </Button>

                {!!categoriesList.length && (
                    <Box
                        mt={{
                            md: 9,
                            xs: 6
                        }}
                    >
                        <CategoriesCarousel
                            categoriesList={categoriesList}
                            linkStructure={`/organization/${organizationDetails.slug}/supporters?sic=${CATEGORY_ID_PLACEHOLDER}`}
                            settings={settings}
                        />
                    </Box>
                )}
            </Section>
            {shouldDisplayBottomDivider && divider}
        </>
    );
};

export default Supporters;
